import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Users, Store, ShieldCheck, Clock, TrendingUp, DollarSign,
  Plus, Edit, Trash2, Search, Eye, Check, X, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => 'Rp ' + (n || 0).toLocaleString('id-ID');

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  suspended: 'bg-gray-100 text-gray-700',
};
const statusLabels = {
  pending: 'Menunggu',
  approved: 'Aktif',
  rejected: 'Ditolak',
  suspended: 'Ditangguhkan',
};

export default function Resellers() {
  const [tab, setTab] = useState('resellers');

  // Stats
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, total_margin: 0, total_spent: 0, total_orders: 0 });

  // Resellers list
  const [resellers, setResellers] = useState([]);
  const [resTotal, setResTotal] = useState(0);
  const [resSearch, setResSearch] = useState('');
  const [resStatus, setResStatus] = useState('');
  const [resPage, setResPage] = useState(1);
  const LIMIT = 30;

  // Tiers
  const [tiers, setTiers] = useState([]);

  // Settings
  const [settings, setSettings] = useState({ auto_approve: false, default_tier_id: '' });

  // Dialogs
  const [detailDialog, setDetailDialog] = useState(false);
  const [tierDialog, setTierDialog] = useState(false);
  const [editResellerDialog, setEditResellerDialog] = useState(false);

  // Detail
  const [detailData, setDetailData] = useState(null);

  // Tier form
  const [tierForm, setTierForm] = useState({ name: '', level: 1, discount_type: 'percentage', discount_value: 10, min_order_count: 0 });
  const [editTierId, setEditTierId] = useState(null);

  // Reseller edit form
  const [editResForm, setEditResForm] = useState({ tier_id: '', status: '', notes: '' });
  const [editResId, setEditResId] = useState(null);

  const [loading, setLoading] = useState(false);

  // ──────────── Fetch ────────────

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/reseller-stats');
      setStats(data);
    } catch {}
  }, []);

  const fetchResellers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (resSearch) params.set('search', resSearch);
      if (resStatus) params.set('status', resStatus);
      params.set('skip', ((resPage - 1) * LIMIT).toString());
      params.set('limit', LIMIT.toString());
      const { data } = await api.get(`/tenant/resellers?${params}`);
      setResellers(data.resellers);
      setResTotal(data.total);
    } catch {}
  }, [resSearch, resStatus, resPage]);

  const fetchTiers = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/reseller-tiers');
      setTiers(data.tiers);
    } catch {}
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/reseller-settings');
      setSettings(data);
    } catch {}
  }, []);

  useEffect(() => { fetchStats(); fetchTiers(); fetchSettings(); }, [fetchStats, fetchTiers, fetchSettings]);
  useEffect(() => { fetchResellers(); }, [fetchResellers]);

  // ──────────── Handlers ────────────

  const handleSaveTier = async () => {
    setLoading(true);
    try {
      if (editTierId) {
        await api.put(`/tenant/reseller-tiers/${editTierId}`, tierForm);
        toast.success('Tier berhasil diupdate');
      } else {
        await api.post('/tenant/reseller-tiers', tierForm);
        toast.success('Tier berhasil dibuat');
      }
      setTierDialog(false);
      setEditTierId(null);
      setTierForm({ name: '', level: 1, discount_type: 'percentage', discount_value: 10, min_order_count: 0 });
      fetchTiers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan tier');
    }
    setLoading(false);
  };

  const handleDeleteTier = async (id) => {
    if (!window.confirm('Hapus tier ini?')) return;
    try {
      await api.delete(`/tenant/reseller-tiers/${id}`);
      toast.success('Tier dihapus');
      fetchTiers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus tier');
    }
  };

  const openEditTier = (t) => {
    setEditTierId(t.id);
    setTierForm({ name: t.name, level: t.level, discount_type: t.discount_type, discount_value: t.discount_value, min_order_count: t.min_order_count || 0 });
    setTierDialog(true);
  };

  const openDetail = async (reseller) => {
    try {
      const { data } = await api.get(`/tenant/resellers/${reseller.id}`);
      setDetailData(data);
      setDetailDialog(true);
    } catch {}
  };

  const openEditReseller = (r) => {
    setEditResId(r.id);
    setEditResForm({ tier_id: r.tier_id || '', status: r.status, notes: r.notes || '' });
    setEditResellerDialog(true);
  };

  const handleSaveReseller = async () => {
    setLoading(true);
    try {
      await api.put(`/tenant/resellers/${editResId}`, editResForm);
      toast.success('Reseller diupdate');
      setEditResellerDialog(false);
      fetchResellers();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal update reseller');
    }
    setLoading(false);
  };

  const quickAction = async (id, status) => {
    try {
      await api.put(`/tenant/resellers/${id}`, { status });
      toast.success(status === 'approved' ? 'Reseller disetujui' : 'Reseller ditolak');
      fetchResellers();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await api.put('/tenant/reseller-settings', settings);
      toast.success('Pengaturan disimpan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(resTotal / LIMIT) || 1;

  // ──────────── Render ────────────

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Reseller Management</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="w-4 h-4" /> Total Reseller</div>
          <div className="text-2xl font-bold mt-1">{stats.total}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4" /> Menunggu Approval</div>
          <div className="text-2xl font-bold mt-1 text-yellow-600">{stats.pending}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" /> Total Order Reseller</div>
          <div className="text-2xl font-bold mt-1">{stats.total_orders}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><DollarSign className="w-4 h-4" /> Total Margin</div>
          <div className="text-2xl font-bold mt-1 text-green-600">{fmt(stats.total_margin)}</div>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="resellers">Reseller</TabsTrigger>
          <TabsTrigger value="tiers">Tier & Harga</TabsTrigger>
          <TabsTrigger value="settings">Pengaturan</TabsTrigger>
        </TabsList>

        {/* ── Tab: Reseller List ── */}
        <TabsContent value="resellers">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Daftar Reseller</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Cari nama, email, toko..." className="pl-9"
                    value={resSearch} onChange={e => { setResSearch(e.target.value); setResPage(1); }} />
                </div>
                <Select value={resStatus} onValueChange={v => { setResStatus(v === 'all' ? '' : v); setResPage(1); }}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Semua Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Status</SelectItem>
                    <SelectItem value="pending">Menunggu</SelectItem>
                    <SelectItem value="approved">Aktif</SelectItem>
                    <SelectItem value="rejected">Ditolak</SelectItem>
                    <SelectItem value="suspended">Ditangguhkan</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left">
                    <th className="py-2 px-2">Nama</th>
                    <th className="py-2 px-2">Toko</th>
                    <th className="py-2 px-2">Tier</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2 text-right">Order</th>
                    <th className="py-2 px-2 text-right">Total Belanja</th>
                    <th className="py-2 px-2 text-right">Margin</th>
                    <th className="py-2 px-2">Aksi</th>
                  </tr></thead>
                  <tbody>
                    {resellers.map(r => (
                      <tr key={r.id} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-2">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="py-2 px-2">{r.store_name}</td>
                        <td className="py-2 px-2"><Badge variant="outline">{r.tier_name || '-'}</Badge></td>
                        <td className="py-2 px-2">
                          <Badge className={statusColors[r.status]}>{statusLabels[r.status] || r.status}</Badge>
                        </td>
                        <td className="py-2 px-2 text-right">{r.total_orders}</td>
                        <td className="py-2 px-2 text-right">{fmt(r.total_spent)}</td>
                        <td className="py-2 px-2 text-right">{fmt(r.total_margin)}</td>
                        <td className="py-2 px-2">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openDetail(r)}><Eye className="w-4 h-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => openEditReseller(r)}><Edit className="w-4 h-4" /></Button>
                            {r.status === 'pending' && (
                              <>
                                <Button size="icon" variant="ghost" className="text-green-600" onClick={() => quickAction(r.id, 'approved')}><Check className="w-4 h-4" /></Button>
                                <Button size="icon" variant="ghost" className="text-red-600" onClick={() => quickAction(r.id, 'rejected')}><X className="w-4 h-4" /></Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {resellers.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Belum ada reseller terdaftar</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">Halaman {resPage} dari {totalPages} ({resTotal} reseller)</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={resPage <= 1} onClick={() => setResPage(p => p - 1)}>Prev</Button>
                    <Button size="sm" variant="outline" disabled={resPage >= totalPages} onClick={() => setResPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Tiers ── */}
        <TabsContent value="tiers">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Tier Reseller</CardTitle>
              <Button size="sm" onClick={() => { setEditTierId(null); setTierForm({ name: '', level: 1, discount_type: 'percentage', discount_value: 10, min_order_count: 0 }); setTierDialog(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Tambah Tier
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left">
                    <th className="py-2 px-2">Level</th>
                    <th className="py-2 px-2">Nama</th>
                    <th className="py-2 px-2">Diskon</th>
                    <th className="py-2 px-2">Min. Order (Auto-upgrade)</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Aksi</th>
                  </tr></thead>
                  <tbody>
                    {tiers.map(t => (
                      <tr key={t.id} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-2 font-mono">{t.level}</td>
                        <td className="py-2 px-2 font-medium">{t.name}</td>
                        <td className="py-2 px-2">
                          {t.discount_type === 'percentage' ? `${t.discount_value}%` : fmt(t.discount_value)}
                        </td>
                        <td className="py-2 px-2">{t.min_order_count || 'Manual'}</td>
                        <td className="py-2 px-2">
                          <Badge className={t.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                            {t.is_active !== false ? 'Aktif' : 'Nonaktif'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openEditTier(t)}><Edit className="w-4 h-4" /></Button>
                            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDeleteTier(t.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tiers.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Belum ada tier. Buat tier pertama untuk memulai program reseller.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Settings ── */}
        <TabsContent value="settings">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Pengaturan Reseller</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Auto Approve</Label>
                  <p className="text-sm text-muted-foreground">Otomatis setujui pendaftaran reseller baru</p>
                </div>
                <Switch checked={settings.auto_approve} onCheckedChange={v => setSettings({ ...settings, auto_approve: v })} />
              </div>

              <div className="space-y-2">
                <Label>Default Tier</Label>
                <Select value={settings.default_tier_id || 'none'} onValueChange={v => setSettings({ ...settings, default_tier_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="w-[250px]"><SelectValue placeholder="Pilih tier default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ada</SelectItem>
                    {tiers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} (Level {t.level})</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">Tier yang diberikan saat reseller baru disetujui</p>
              </div>

              <Button onClick={handleSaveSettings} disabled={loading}>Simpan Pengaturan</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Dialog: Tier Form ── */}
      <Dialog open={tierDialog} onOpenChange={setTierDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editTierId ? 'Edit Tier' : 'Tambah Tier'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Tier</Label>
              <Input placeholder="e.g. Bronze, Silver, Gold" value={tierForm.name} onChange={e => setTierForm({ ...tierForm, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Level (semakin tinggi = diskon lebih besar)</Label>
              <Input type="number" min="1" value={tierForm.level} onChange={e => setTierForm({ ...tierForm, level: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="space-y-2">
              <Label>Tipe Diskon</Label>
              <Select value={tierForm.discount_type} onValueChange={v => setTierForm({ ...tierForm, discount_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Persentase (%)</SelectItem>
                  <SelectItem value="fixed">Nominal Tetap (Rp)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nilai Diskon {tierForm.discount_type === 'percentage' ? '(%)' : '(Rp)'}</Label>
              <Input type="number" min="0" value={tierForm.discount_value} onChange={e => setTierForm({ ...tierForm, discount_value: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="space-y-2">
              <Label>Min. Order untuk Auto-upgrade (0 = manual)</Label>
              <Input type="number" min="0" value={tierForm.min_order_count} onChange={e => setTierForm({ ...tierForm, min_order_count: parseInt(e.target.value) || 0 })} />
            </div>
            <Button className="w-full" onClick={handleSaveTier} disabled={loading || !tierForm.name}>
              {editTierId ? 'Update Tier' : 'Buat Tier'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Reseller Detail ── */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detail Reseller</DialogTitle></DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Nama</Label>
                  <div className="font-medium">{detailData.reseller.name}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <div>{detailData.reseller.email}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Nama Toko</Label>
                  <div className="font-medium">{detailData.reseller.store_name}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Telepon</Label>
                  <div>{detailData.reseller.phone || '-'}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Tier</Label>
                  <div><Badge variant="outline">{detailData.reseller.tier_name || '-'}</Badge></div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div><Badge className={statusColors[detailData.reseller.status]}>{statusLabels[detailData.reseller.status]}</Badge></div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Kode Reseller</Label>
                  <div className="font-mono">{detailData.reseller.code}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Alamat Toko</Label>
                  <div>{detailData.reseller.store_address || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Card><CardContent className="pt-3 text-center">
                  <div className="text-sm text-muted-foreground">Total Order</div>
                  <div className="text-xl font-bold">{detailData.reseller.total_orders}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-3 text-center">
                  <div className="text-sm text-muted-foreground">Total Belanja</div>
                  <div className="text-xl font-bold">{fmt(detailData.reseller.total_spent)}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-3 text-center">
                  <div className="text-sm text-muted-foreground">Total Margin</div>
                  <div className="text-xl font-bold text-green-600">{fmt(detailData.reseller.total_margin)}</div>
                </CardContent></Card>
              </div>

              {detailData.reseller.bank_name && (
                <div className="p-3 bg-muted rounded-md">
                  <Label className="text-muted-foreground text-xs">Bank</Label>
                  <div className="text-sm">{detailData.reseller.bank_name} - {detailData.reseller.bank_account_number} ({detailData.reseller.bank_account_name})</div>
                </div>
              )}

              {detailData.orders && detailData.orders.length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">Order Terakhir</Label>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left">
                        <th className="py-1 px-2">No. Order</th>
                        <th className="py-1 px-2 text-right">Total</th>
                        <th className="py-1 px-2 text-right">Margin</th>
                        <th className="py-1 px-2">Dropship</th>
                        <th className="py-1 px-2">Tanggal</th>
                      </tr></thead>
                      <tbody>
                        {detailData.orders.map(o => (
                          <tr key={o.id} className="border-b">
                            <td className="py-1 px-2 font-mono text-xs">{o.order_number}</td>
                            <td className="py-1 px-2 text-right">{fmt(o.total)}</td>
                            <td className="py-1 px-2 text-right text-green-600">{fmt(o.reseller_margin || 0)}</td>
                            <td className="py-1 px-2">{o.is_dropship ? <Badge className="bg-blue-100 text-blue-700">Dropship</Badge> : '-'}</td>
                            <td className="py-1 px-2 text-xs">{new Date(o.created_at).toLocaleDateString('id-ID')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Edit Reseller ── */}
      <Dialog open={editResellerDialog} onOpenChange={setEditResellerDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Reseller</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editResForm.status} onValueChange={v => setEditResForm({ ...editResForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Menunggu</SelectItem>
                  <SelectItem value="approved">Aktif</SelectItem>
                  <SelectItem value="rejected">Ditolak</SelectItem>
                  <SelectItem value="suspended">Ditangguhkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select value={editResForm.tier_id || 'none'} onValueChange={v => setEditResForm({ ...editResForm, tier_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Pilih tier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tidak ada</SelectItem>
                  {tiers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} (Level {t.level})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Catatan Admin</Label>
              <Input value={editResForm.notes} onChange={e => setEditResForm({ ...editResForm, notes: e.target.value })} placeholder="Catatan internal..." />
            </div>
            <Button className="w-full" onClick={handleSaveReseller} disabled={loading}>Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
